import React from 'react';

import Button from '../Input2/Button';
import Popup from '../Popup/Popup';
import { CallOutcomePicker } from '../Softphone/CallOutcomePicker';
import { General } from '../StrokeIcons';
import { WaveSpinner } from '../WaveSpinner';

export type CallStepActionsPureProps = {
  /** Cadence `call` step id. Passed to the injected dialer and to `onMarkCallStepDone`. */
  cadenceStepId: string;
  /**
   * Injected dialer for the `call` step. Each surface (dashboard / extension) passes a
   * function that returns its own click-to-call button wired to this step id, so logging
   * the call outcome completes the step.
   */
  renderCadenceCallButton?: (args: { cadenceStepId: string }) => React.ReactNode;
  /**
   * Completes a `call` step the rep handled outside the dialer (called from their own
   * phone, or dialed then skipped the disposition). Opens the disposition picker, then
   * records the outcome — deduped server-side by `cadenceStepId` so the step ends with
   * exactly one call record. When omitted, the "Mark as done" affordance is hidden.
   */
  onMarkCallStepDone?: (args: { cadenceStepId: string; outcome: string; notes?: string }) => Promise<void> | void;
};

/**
 * The interactive affordances for an action-required `call` step: the injected dialer
 * button plus a "Mark as done" button (for calls placed outside the dialer) that opens
 * the {@link CallOutcomePicker}. Shared by the Action-tab card
 * (`CadenceReminderActionCardPure`) and the Interact-tab timeline row
 * (`CampaignStepRowPure`) so the two surfaces never drift.
 */
export function CallStepActionsPure({
  cadenceStepId,
  renderCadenceCallButton,
  onMarkCallStepDone,
}: CallStepActionsPureProps) {
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  const handleSelectOutcome = async (outcome: string) => {
    if (!onMarkCallStepDone) return;
    setSubmitting(true);
    try {
      await onMarkCallStepDone({ cadenceStepId, outcome });
      setPickerOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {renderCadenceCallButton?.({ cadenceStepId })}
      {onMarkCallStepDone && (
        <Button
          color="green"
          tone="outline"
          icon={submitting ? <WaveSpinner size={16} /> : <General.CheckSquareBroken />}
          disabled={submitting}
          tooltip="I made this call — mark the step done"
          onClick={() => setPickerOpen(true)}>
          Mark as done
        </Button>
      )}
      {pickerOpen && (
        <Popup onClose={() => !submitting && setPickerOpen(false)}>
          <div className="flex w-80 flex-col gap-1 rounded-xl bg-white p-5 dark:bg-gray-900">
            <span className="text-center text-base font-semibold text-gray-900 dark:text-gray-100">Log the call</span>
            <CallOutcomePicker
              onSelect={(outcome) => void handleSelectOutcome(outcome)}
              onSkip={() => setPickerOpen(false)}
              disabled={submitting}
            />
          </div>
        </Popup>
      )}
    </>
  );
}
